import React, { useState } from 'react';
import { Button, Space, Table, Tag } from 'antd';
import ResolveFollowupModal from '../../tasks/components/ResolveFollowupModal';
import SnoozeFollowupModal from '../../tasks/components/SnoozeFollowupModal';
import { useResolveFollowupTask } from '../../tasks/hooks/useResolveFollowupTask';
import { useSnoozeFollowupTask } from '../../tasks/hooks/useSnoozeFollowupTask';
import { PlusOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTaskItems } from '../../tasks/hooks/useTaskItems';
import TaskFormDrawer from '../../tasks/components/TaskFormDrawer';
import TaskDetailDrawer from '../../tasks/components/TaskDetailDrawer';
import {
  TASK_STATUS_LABELS,
  TASK_PRIORITY_LABELS,
  TASK_PRIORITY_COLORS,
  isOpenFollowup,
  isTerminalStatus,
  normalizeTaskStatus,
  normalizeTaskType,
} from '../../tasks/constants';
import {
  ACTIVITIES_PANEL_TITLE,
  ACTIVITIES_PANEL_EMPTY_LEAD,
  ACTIVITIES_NEW_FOLLOWUP,
} from '../../tasks/copy';
import type { TaskItemListItem, TaskItemStatus, TaskPriority } from '../../tasks/types';
import Can from '../../permissions/Can';
import { usePermission } from '../../permissions/PermissionsContext';

function formatDate(s: string | null | undefined): string {
  if (s == null || s === '') return '—';
  try {
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? s : d.toLocaleDateString();
  } catch {
    return '—';
  }
}

export interface LeadTasksPanelProps {
  leadId: string;
  contactId: string;
}

const LeadTasksPanel: React.FC<LeadTasksPanelProps> = ({ leadId, contactId }) => {
  const canView = usePermission('tasks.view');
  const [formOpen, setFormOpen] = useState(false);
  const [editTaskId, setEditTaskId] = useState<string | null>(null);
  const [viewTaskId, setViewTaskId] = useState<string | null>(null);
  const [resolveTaskId, setResolveTaskId] = useState<string | null>(null);
  const [snoozeTaskId, setSnoozeTaskId] = useState<string | null>(null);

  const resolveMutation = useResolveFollowupTask(() => setResolveTaskId(null));
  const snoozeMutation = useSnoozeFollowupTask(() => setSnoozeTaskId(null));

  const { data: paged, isLoading } = useTaskItems(
    { leadId, pageSize: 20 },
    { enabled: canView }
  );

  if (!canView) {
    return null;
  }

  const columns: ColumnsType<TaskItemListItem> = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (title: string, record) => (
        <a onClick={() => setViewTaskId(record.id)}>{title}</a>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: TaskItemStatus) => TASK_STATUS_LABELS[status],
    },
    {
      title: 'Issue date',
      dataIndex: 'createdAtUtc',
      key: 'createdAtUtc',
      width: 110,
      render: (date: string | null | undefined) => formatDate(date),
    },
    {
      title: 'Due Date',
      dataIndex: 'dueDate',
      key: 'dueDate',
      width: 110,
      render: (date: string | null) => formatDate(date),
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      width: 100,
      render: (priority: TaskPriority) => (
        <Tag color={TASK_PRIORITY_COLORS[priority]}>
          {TASK_PRIORITY_LABELS[priority]}
        </Tag>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 200,
      render: (_, record) => {
        const showFollowupActions = isOpenFollowup(
          normalizeTaskType(record),
          normalizeTaskStatus(record.status),
        );
        const isTerminal = isTerminalStatus(normalizeTaskStatus(record.status));
        return (
          <Can permission="tasks.edit">
            <Space size={0}>
              {showFollowupActions && (
                <>
                  <Button type="link" size="small" onClick={() => setResolveTaskId(record.id)}>
                    Mark as completed
                  </Button>
                  <Button type="link" size="small" onClick={() => setSnoozeTaskId(record.id)}>
                    Snooze
                  </Button>
                </>
              )}
              {!isTerminal && (
                <Button
                  type="link"
                  size="small"
                  onClick={() => {
                    setEditTaskId(record.id);
                    setFormOpen(true);
                  }}
                >
                  Edit
                </Button>
              )}
            </Space>
          </Can>
        );
      },
    },
  ];

  return (
    <>
      <Space style={{ marginBottom: 12, width: '100%', justifyContent: 'space-between' }}>
        <strong>{ACTIVITIES_PANEL_TITLE}</strong>
        <Can permission="tasks.create">
          <Button
            type="primary"
            size="small"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditTaskId(null);
              setFormOpen(true);
            }}
          >
            {ACTIVITIES_NEW_FOLLOWUP}
          </Button>
        </Can>
      </Space>

      <Table
        size="small"
        columns={columns}
        dataSource={paged?.items ?? []}
        loading={isLoading}
        rowKey="id"
        pagination={false}
        locale={{ emptyText: ACTIVITIES_PANEL_EMPTY_LEAD }}
      />

      <TaskFormDrawer
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditTaskId(null);
        }}
        onSuccess={() => {
          setFormOpen(false);
          setEditTaskId(null);
        }}
        editTaskId={editTaskId}
        defaultTaskType={1}
        initialContactId={contactId}
        initialLeadId={leadId}
        lockContactAndLead
      />

      <TaskDetailDrawer
        open={!!viewTaskId}
        onClose={() => setViewTaskId(null)}
        taskId={viewTaskId}
      />

      <ResolveFollowupModal
        open={resolveTaskId != null}
        taskId={resolveTaskId}
        loading={resolveMutation.isPending}
        onCancel={() => setResolveTaskId(null)}
        onSubmit={(taskId, payload) => resolveMutation.mutate({ taskId, payload })}
      />

      <SnoozeFollowupModal
        open={snoozeTaskId != null}
        taskId={snoozeTaskId}
        loading={snoozeMutation.isPending}
        onCancel={() => setSnoozeTaskId(null)}
        onSubmit={(taskId, newDueDate, reason) =>
          snoozeMutation.mutate({ taskId, newDueDate, reason })
        }
      />
    </>
  );
};

export default LeadTasksPanel;
